import { useDataTableFeatures } from "@/components/data-table/hooks/useDataTableFeatures";
import { useDataTableSettings } from "@/components/data-table/hooks/useDataTableSettings";
import useTableColumnVisibility from "@/components/data-table/hooks/useTableColumnVisibility";
import {
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarSeparator,
  MenubarTrigger,
} from "@/components/ui/menubar";
import { RotateCcw } from "lucide-react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

export interface AdditionalResetItem {
  label: string;
  onReset: () => void;
}

interface QueryTableResetMenuBarMenuProps {
  disabled?: boolean;
  inDialog?: boolean;
  additionalResetItems?: AdditionalResetItem[];
}

export default function QueryTableResetMenuBarMenu<TData>({
  disabled,
  inDialog,
  additionalResetItems = [],
}: QueryTableResetMenuBarMenuProps) {
  const { t } = useTranslation();
  const { table, options } = useDataTableSettings<TData>();
  const { resetColumnVisibility } = useTableColumnVisibility();

  const onResetSorting = useCallback(() => {
    options.resetSorting();
  }, [options]);

  const onResetFilters = useCallback(() => {
    options.resetFilters();
  }, [options]);

  const onResetPinnedColumns = useCallback(() => {
    table.setColumnPinning({});
  }, [table]);

  const onResetColumnVisibility = useCallback(() => {
    resetColumnVisibility();
  }, [resetColumnVisibility]);

  const onResetColumnOrder = useCallback(() => {
    table.setColumnOrder(table.getAllColumns().map((column) => column.id));
  }, [table]);

  const onResetAll = useCallback(() => {
    onResetPinnedColumns();
    onResetColumnVisibility();
    onResetColumnOrder();
    onResetSorting();
    onResetFilters();
    for (const item of additionalResetItems) {
      item.onReset();
    }
  }, [
    onResetPinnedColumns,
    onResetColumnVisibility,
    onResetColumnOrder,
    onResetSorting,
    onResetFilters,
    additionalResetItems,
  ]);

  const {
    enableColumnPinning,
    enableSorting,
    enableFilters,
    enableColumnVisibility,
    enableColumnOrder,
  } = useDataTableFeatures();

  return (
    <MenubarMenu>
      <MenubarTrigger disabled={disabled}>
        {t("listMenubar.reset.trigger")}
      </MenubarTrigger>
      <MenubarContent className={inDialog ? "pointer-events-auto" : ""}>
        <MenubarItem onClick={onResetFilters} disabled={!enableFilters}>
          {t("listMenubar.reset.resetFilters")}
        </MenubarItem>
        <MenubarItem onClick={onResetSorting} disabled={!enableSorting}>
          {t("listMenubar.reset.resetSorting")}
        </MenubarItem>
        <MenubarItem
          onClick={onResetPinnedColumns}
          disabled={!enableColumnPinning}
        >
          {t("listMenubar.reset.resetPinnedColumns")}
        </MenubarItem>
        <MenubarItem
          onClick={onResetColumnVisibility}
          disabled={!enableColumnVisibility}
        >
          {t("listMenubar.reset.resetColumnVisibility")}
        </MenubarItem>
        <MenubarItem onClick={onResetColumnOrder} disabled={!enableColumnOrder}>
          {t("listMenubar.reset.resetColumnOrder")}
        </MenubarItem>
        {additionalResetItems.map((item) => (
          <MenubarItem key={item.label} onClick={item.onReset}>
            {item.label}
          </MenubarItem>
        ))}
        <MenubarSeparator />
        <MenubarItem onClick={onResetAll} disabled={!enableSorting}>
          <RotateCcw className="w-4 h-4 mr-2" />
          {t("listMenubar.reset.resetAll")}
        </MenubarItem>
      </MenubarContent>
    </MenubarMenu>
  );
}
